import Spinner from "../../components/ui/Spinner";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useCallback, useEffect, useState } from "react";
import { formatISTDateTime } from "../../utils/dateUtils";

function downloadUrl(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
import { adminService } from "../../features/admin/service";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import TemplateFormModal from "../../components/admin/templates/TemplateFormModal";
import RefreshButton from "../../components/admin/RefreshButton";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useAdminToast } from "../../context/AdminToastContext";
import type {

  AdminTemplate,
  AdminTemplateCategory,
  AdminTemplateFromAssetDraft,
  AdminUserAsset,
} from "../../features/admin/types";

type TemplateAssetType = "flyer" | "video" | "itinerary";
type Tab = TemplateAssetType | "user-assets";

const TABS: { key: Tab; label: string }[] = [
  { key: "flyer", label: "Flyer" },
  { key: "video", label: "Video" },
  { key: "itinerary", label: "Itinerary" },
  { key: "user-assets", label: "User Assets" },
];

const ASSET_TYPE_FILTERS = ["", "flyer", "video", "itinerary", "slides"];
const DEFAULT_PAGE_SIZE = 8;
const DEFAULT_TEMPLATE_PAGE_SIZE = 8;

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="px-2 py-0.5 rounded-full bg-indigo-900/40 text-indigo-300 text-[10px] font-semibold uppercase tracking-wide">{children}</span>;
}

export default function AdminTemplatesPage() {
  const [tab, setTab] = useState<Tab>("flyer");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);

  // Templates state
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [categories, setCategories] = useState<AdminTemplateCategory[]>([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [templatePage, setTemplatePage] = useState(0);
  const [templatePageSize, setTemplatePageSize] = useState(DEFAULT_TEMPLATE_PAGE_SIZE);

  // Form modal
  const [formOpen, setFormOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<AdminTemplate | null>(null);
  const [draft, setDraft] = useState<AdminTemplateFromAssetDraft | null>(null);
  const [formAssetType, setFormAssetType] = useState<TemplateAssetType>("flyer");
  const [deleteTarget, setDeleteTarget] = useState<AdminTemplate | null>(null);
  const [viewTemplate, setViewTemplate] = useState<AdminTemplate | null>(null);

  // User assets state
  const [assets, setAssets] = useState<AdminUserAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetTotal, setAssetTotal] = useState(0);
  const [assetSkip, setAssetSkip] = useState(0);
  const [assetPageSize, setAssetPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const [userSearchFilter, setUserSearchFilter] = useState("");
  const [expandedAsset, setExpandedAsset] = useState<AdminUserAsset | null>(null);
  useBodyScrollLock(!!viewTemplate || !!expandedAsset);
  const [usingAsset, setUsingAsset] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const res = await adminService.getAdminTemplateCategories();
      setCategories(res.categories ?? []);
    } catch { /* non-blocking */ }
  }, []);

  const loadTemplates = useCallback(async (assetType: TemplateAssetType) => {
    setLoadingTemplates(true); setError(null);
    try {
      const res = await adminService.getAdminTemplates(assetType);
      setTemplates(res.templates ?? []);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadAssets = useCallback(async (skip: number) => {
    setLoadingAssets(true); setError(null);
    try {
      const res = await adminService.getAdminUserAssets({
        asset_type: assetTypeFilter || undefined,
        search: userSearchFilter.trim() || undefined,
        skip,
        limit: assetPageSize,
      });
      setAssets(res.assets ?? []);
      setAssetTotal(res.total ?? 0);
      setAssetSkip(skip);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoadingAssets(false);
    }
  }, [assetTypeFilter, userSearchFilter, assetPageSize]);

  useEffect(() => { void loadCategories(); }, [loadCategories]);

  useEffect(() => {
    setCategoryFilter("");
    setTemplatePage(0);
    if (tab === "user-assets") {
      void loadAssets(0);
    } else {
      void loadTemplates(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { setTemplatePage(0); }, [categoryFilter, templatePageSize]);

  useEffect(() => {
    if (tab === "user-assets") void loadAssets(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetPageSize]);

  const templateCategoryOptions = Array.from(
    new Map(templates.map((t) => [t.category || "", t.category_label || t.category || "Uncategorized"])).entries()
  )
    .filter(([slug]) => slug)
    .sort((a, b) => a[1].localeCompare(b[1]));

  const filteredTemplates = categoryFilter
    ? templates.filter((t) => (t.category || "") === categoryFilter)
    : templates;

  const pagedTemplates = filteredTemplates.slice(
    templatePage * templatePageSize,
    templatePage * templatePageSize + templatePageSize
  );

  const openCreate = (assetType: TemplateAssetType) => {
    setFormAssetType(assetType);
    setEditTemplate(null);
    setDraft(null);
    setFormOpen(true);
  };

  const openEdit = (template: AdminTemplate) => {
    setFormAssetType(tab as TemplateAssetType);
    setEditTemplate(template);
    setDraft(null);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setFormOpen(false);
    setSuccess(editTemplate ? "Template updated." : "Template created.");
    setEditTemplate(null);
    setDraft(null);
    if (tab !== "user-assets") void loadTemplates(tab);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminService.deleteAdminTemplate(tab as TemplateAssetType, deleteTarget.id);
      setSuccess("Template deleted.");
      setDeleteTarget(null);
      void loadTemplates(tab as TemplateAssetType);
    } catch (err) {
      setError(parseApiError(err).message);
      setDeleteTarget(null);
    }
  };

  const handleUseAsTemplate = async (asset: AdminUserAsset) => {
    if (!asset.s3_key) { setError("Asset has no source key to reuse."); return; }
    const at = (["flyer", "video", "itinerary"].includes(asset.asset_type) ? asset.asset_type : "flyer") as TemplateAssetType;
    setUsingAsset(true); setError(null);
    try {
      const d = await adminService.useAssetAsTemplate({
        user_id: asset.user_id,
        session_id: asset.session_id,
        s3_key: asset.s3_key,
        asset_type: asset.asset_type,
      });
      setFormAssetType((["flyer", "video", "itinerary"].includes(d.asset_type) ? d.asset_type : at) as TemplateAssetType);
      setEditTemplate(null);
      setDraft(d);
      setExpandedAsset(null);
      setFormOpen(true);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setUsingAsset(false);
    }
  };

  const isTemplateTab = tab !== "user-assets";

  return (
    <>
      <div>
        <AdminPageHeader
          title="Templates"
          subtitle="Manage flyer, video and itinerary templates, and browse user-generated assets."
        />

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1 border-b border-gray-800 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setError(null); setSuccess(null); }}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-indigo-500 text-indigo-400" : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
        {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

        {/* Template tabs */}
        {isTemplateTab && (
          <>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-sm font-semibold text-white">
                {tab.charAt(0).toUpperCase() + tab.slice(1)} Templates
                <span className="text-gray-500 font-normal ml-1">
                  ({categoryFilter ? filteredTemplates.length : templates.length})
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                  <option value="">All categories</option>
                  {templateCategoryOptions.map(([slug, label]) => (
                    <option key={slug} value={slug}>{label}</option>
                  ))}
                </select>
                <button onClick={() => void loadTemplates(tab as TemplateAssetType)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm text-gray-300 transition-colors">
                  Refresh
                </button>
                <button onClick={() => openCreate(tab as TemplateAssetType)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors">
                  + New Template
                </button>
              </div>
            </div>

            {loadingTemplates ? (
              <div className="py-12 text-center text-gray-500 text-sm"><Spinner /></div>
            ) : filteredTemplates.length === 0 ? (
              <div className="py-12 text-center text-gray-500 text-sm">No templates found.</div>
            ) : (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {pagedTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => setViewTemplate(tpl)}
                    className="text-left bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-indigo-500/40 transition-colors group"
                  >
                    <div className="aspect-[4/3] bg-gray-800 overflow-hidden flex items-center justify-center">
                      {tpl.preview_image_url ? (
                        <img src={tpl.preview_image_url} alt={tpl.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{tpl.emoji || "🖼️"}</span>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-sm font-medium text-white truncate">{tpl.name}</h3>
                        <Badge>{tpl.asset_type}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{tpl.category_label || tpl.category || "—"}</p>

                      {/* Template ID — copyable */}
                      <div
                        className="mt-1.5 flex items-center gap-1 cursor-pointer group/id"
                        title="Click to copy template ID"
                        onClick={(e) => { e.stopPropagation(); void navigator.clipboard.writeText(tpl.id); }}
                      >
                        <span className="text-[9px] font-mono text-gray-600 group-hover/id:text-indigo-400 truncate transition-colors">{tpl.id}</span>
                        <span className="text-[9px] text-gray-700 group-hover/id:text-indigo-400 transition-colors">⎘</span>
                      </div>

                      {/* HTML template badge for itinerary */}
                      {tpl.asset_type === "itinerary" && tpl.html_template_id && (
                        <div className="mt-1 flex items-center gap-1">
                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-400 border border-emerald-800/40 truncate max-w-[140px]">
                            {tpl.html_template_id}
                          </span>
                          {tpl.html_template_url ? (
                            <span
                              role="button"
                              tabIndex={0}
                              title="Preview HTML template"
                              onClick={(e) => { e.stopPropagation(); window.open(tpl.html_template_url!, "_blank", "noopener"); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.open(tpl.html_template_url!, "_blank", "noopener"); } }}
                              className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-900/30 hover:bg-emerald-900/60 text-emerald-400 transition-colors cursor-pointer"
                            >
                              View HTML
                            </span>
                          ) : null}
                        </div>
                      )}

                      {tpl.asset_type === "itinerary" && !tpl.html_template_id && (
                        <div className="mt-1">
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-900/30 text-yellow-600 border border-yellow-800/30">
                            No HTML template
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-gray-600">{tpl.aspect_ratio || ""}</span>
                        <div className="flex gap-1">
                          {tpl.preview_image_url && (
                            <span
                              role="button"
                              tabIndex={0}
                              title="Download preview"
                              onClick={(e) => { e.stopPropagation(); downloadUrl(tpl.preview_image_url!, `${tpl.name.replace(/\s+/g, "_")}_preview.jpg`); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); downloadUrl(tpl.preview_image_url!, `${tpl.name.replace(/\s+/g, "_")}_preview.jpg`); } }}
                              className="px-2 py-0.5 bg-indigo-900/30 hover:bg-indigo-900/60 text-indigo-400 rounded-lg text-[10px] transition-colors"
                            >
                              ↓
                            </span>
                          )}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(tpl); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setDeleteTarget(tpl); } }}
                            className="px-2 py-0.5 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-[10px] transition-colors"
                          >
                            Delete
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                page={templatePage}
                pageSize={templatePageSize}
                total={filteredTemplates.length}
                onPageChange={setTemplatePage}
                onPageSizeChange={setTemplatePageSize}
              />
              </>
            )}
          </>
        )}

        {/* User Assets tab */}
        {tab === "user-assets" && (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Asset Type</label>
                <select
                  value={assetTypeFilter}
                  onChange={(e) => setAssetTypeFilter(e.target.value)}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                  {ASSET_TYPE_FILTERS.map((v) => <option key={v || "all"} value={v}>{v ? v.charAt(0).toUpperCase() + v.slice(1) : "All"}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Search User</label>
                <input
                  value={userSearchFilter}
                  onChange={(e) => setUserSearchFilter(e.target.value)}
                  placeholder="Name, email or phone"
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 placeholder-gray-500"
                />
              </div>
              <button onClick={() => void loadAssets(0)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors">
                Apply
              </button>
              <RefreshButton onRefresh={() => loadAssets(assetSkip)} />
            </div>

            {loadingAssets ? (
              <div className="py-12 text-center text-gray-500 text-sm"><Spinner /></div>
            ) : assets.length === 0 ? (
              <div className="py-12 text-center text-gray-500 text-sm">No assets found.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {assets.map((asset, idx) => (
                    <button
                      key={`${asset.user_id}-${asset.s3_key ?? idx}`}
                      onClick={() => setExpandedAsset(asset)}
                      className="text-left bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-indigo-500/40 transition-colors"
                    >
                      <div className="aspect-[4/3] bg-gray-800 overflow-hidden flex items-center justify-center">
                        {asset.thumbnail_url || asset.url ? (
                          <img src={asset.thumbnail_url || asset.url || ""} alt={asset.caption || "asset"} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-3xl">📄</span>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h3 className="text-sm font-medium text-white truncate">{asset.user_name || asset.user_email || asset.user_id}</h3>
                          <Badge>{asset.asset_type}</Badge>
                        </div>
                        {asset.user_email && <p className="text-[10px] text-gray-500 truncate">{asset.user_email}</p>}
                        {asset.user_phone && <p className="text-[10px] text-gray-500 truncate">{asset.user_phone}</p>}
                        {asset.caption && <p className="text-xs text-gray-400 truncate mt-1">{asset.caption}</p>}
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-[10px] text-gray-600">{asset.timestamp ? formatISTDateTime(asset.timestamp) : ""}</p>
                          {(asset.url || asset.video_url) && (
                            <span
                              role="button"
                              tabIndex={0}
                              title="Download asset"
                              onClick={(e) => { e.stopPropagation(); const u = asset.video_url || asset.url || ""; downloadUrl(u, `asset_${asset.asset_type}_${asset.user_id?.slice(-6)}.${asset.type === "video" ? "mp4" : "jpg"}`); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); const u = asset.video_url || asset.url || ""; downloadUrl(u, `asset_${asset.asset_type}_${asset.user_id?.slice(-6)}.${asset.type === "video" ? "mp4" : "jpg"}`); } }}
                              className="px-2 py-0.5 bg-indigo-900/30 hover:bg-indigo-900/60 text-indigo-400 rounded-lg text-[10px] transition-colors"
                            >
                              ↓ Download
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Pagination */}
                <Pagination
                  page={Math.floor(assetSkip / assetPageSize)}
                  pageSize={assetPageSize}
                  total={assetTotal}
                  onPageChange={(p) => void loadAssets(p * assetPageSize)}
                  onPageSizeChange={setAssetPageSize}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Asset detail modal */}
      {expandedAsset && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl shadow-2xl my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-gray-900 rounded-t-2xl z-10">
              <h2 className="text-lg font-semibold text-white">Asset Detail</h2>
              <button onClick={() => setExpandedAsset(null)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-xl overflow-hidden bg-gray-800 flex items-center justify-center">
                {expandedAsset.video_url ? (
                  <video src={expandedAsset.video_url} controls className="w-full max-h-96" />
                ) : expandedAsset.url ? (
                  <img src={expandedAsset.url} alt={expandedAsset.caption || "asset"} className="w-full max-h-96 object-contain" />
                ) : expandedAsset.html_url ? (
                  <a href={expandedAsset.html_url} target="_blank" rel="noreferrer" className="py-6 text-indigo-400 text-sm">Open HTML asset ↗</a>
                ) : (
                  <span className="py-10 text-3xl">📄</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-gray-500">Name</span>
                <span className="text-gray-300">{expandedAsset.user_name || "—"}</span>
                <span className="text-gray-500">Email</span>
                <span className="text-gray-300">{expandedAsset.user_email || "—"}</span>
                <span className="text-gray-500">Phone</span>
                <span className="text-gray-300">{expandedAsset.user_phone || "—"}</span>
                <span className="text-gray-500">User ID</span>
                <span className="text-gray-300 font-mono text-[10px]">{expandedAsset.user_id}</span>
                <span className="text-gray-500">Type</span>
                <span className="text-gray-300">{expandedAsset.asset_type}</span>
                {expandedAsset.caption && (<><span className="text-gray-500">Caption</span><span className="text-gray-300">{expandedAsset.caption}</span></>)}
                {expandedAsset.timestamp && (<><span className="text-gray-500">Created</span><span className="text-gray-300">{formatISTDateTime(expandedAsset.timestamp)}</span></>)}
              </div>

              {(() => {
                const hasMeta = !!expandedAsset.prompt_used
                  || (expandedAsset.field_values && Object.keys(expandedAsset.field_values).length > 0)
                  || (expandedAsset.template_snapshot && Object.keys(expandedAsset.template_snapshot).length > 0);
                if (!hasMeta) {
                  return <p className="text-xs text-gray-500 border-t border-gray-800 pt-4">No generation metadata available for this asset.</p>;
                }
                return (
                  <div className="border-t border-gray-800 pt-4 space-y-4">
                    {expandedAsset.prompt_used && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Prompt Used</label>
                        <textarea readOnly rows={4} value={expandedAsset.prompt_used} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-xs" />
                      </div>
                    )}
                    {expandedAsset.field_values && Object.keys(expandedAsset.field_values).length > 0 && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Field Values</label>
                        <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <tbody className="divide-y divide-gray-800">
                            {Object.entries(expandedAsset.field_values).map(([k, v]) => (
                              <tr key={k}><td className="py-1 pr-3 text-gray-500 align-top">{k}</td><td className="py-1 text-gray-300 break-words">{typeof v === "object" ? JSON.stringify(v) : String(v)}</td></tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    )}
                    {expandedAsset.template_snapshot && Object.keys(expandedAsset.template_snapshot).length > 0 && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Template Snapshot</label>
                        <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <tbody className="divide-y divide-gray-800">
                            {Object.entries(expandedAsset.template_snapshot).map(([k, v]) => (
                              <tr key={k}><td className="py-1 pr-3 text-gray-500 align-top">{k}</td><td className="py-1 text-gray-300 break-words">{typeof v === "object" ? JSON.stringify(v) : String(v)}</td></tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex justify-between items-center border-t border-gray-800 pt-4">
                <button onClick={() => setExpandedAsset(null)} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm">Close</button>
                <div className="flex gap-2">
                  {(expandedAsset.video_url || expandedAsset.url) && (
                    <button
                      onClick={() => {
                        const u = expandedAsset.video_url || expandedAsset.url || "";
                        const ext = expandedAsset.type === "video" ? "mp4" : expandedAsset.asset_type === "itinerary" ? "pdf" : "jpg";
                        downloadUrl(u, `${expandedAsset.asset_type}_${expandedAsset.user_id?.slice(-6)}_${expandedAsset.timestamp?.slice(0,10) || "asset"}.${ext}`);
                      }}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl text-sm transition-colors"
                    >
                      ↓ Download
                    </button>
                  )}
                  {expandedAsset.html_url && expandedAsset.asset_type === "itinerary" && (
                    <button
                      onClick={() => downloadUrl(expandedAsset.html_url!, `itinerary_${expandedAsset.user_id?.slice(-6)}.html`)}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl text-sm transition-colors"
                    >
                      ↓ HTML
                    </button>
                  )}
                  <button
                    disabled={usingAsset || !expandedAsset.s3_key}
                    onClick={() => void handleUseAsTemplate(expandedAsset)}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {usingAsset ? "Preparing…" : "Use as Template"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Template modal */}
      {viewTemplate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setViewTemplate(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white truncate">{viewTemplate.name}</h2>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => { setViewTemplate(null); openEdit(viewTemplate); }}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
                >
                  Edit
                </button>
                <button onClick={() => setViewTemplate(null)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="aspect-[4/3] bg-gray-800 rounded-xl overflow-hidden flex items-center justify-center">
                {viewTemplate.preview_image_url ? (
                  <img src={viewTemplate.preview_image_url} alt={viewTemplate.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl">{viewTemplate.emoji || "🖼️"}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-gray-500">Type</span>
                <span className="text-gray-300">{viewTemplate.asset_type}</span>
                <span className="text-gray-500">Category</span>
                <span className="text-gray-300">{viewTemplate.category_label || viewTemplate.category || "—"}</span>
                <span className="text-gray-500">Aspect Ratio</span>
                <span className="text-gray-300">{viewTemplate.aspect_ratio || "—"}</span>
                <span className="text-gray-500">ID</span>
                <span className="text-gray-300 font-mono text-[10px]">{viewTemplate.id}</span>
                {viewTemplate.asset_type === "itinerary" && (
                  <>
                    <span className="text-gray-500">HTML Template</span>
                    <span className="text-gray-300">
                      {viewTemplate.html_template_id ? (
                        viewTemplate.html_template_url ? (
                          <a href={viewTemplate.html_template_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">
                            {viewTemplate.html_template_id}
                          </a>
                        ) : viewTemplate.html_template_id
                      ) : "None"}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit form */}
      {formOpen && (
        <TemplateFormModal
          assetType={formAssetType}
          template={editTemplate}
          draft={draft}
          categories={categories}
          onClose={() => { setFormOpen(false); setEditTemplate(null); setDraft(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Template"
          description={`"${deleteTarget.name}" will be permanently removed.`}
          items={["Remove template from database", "Delete associated S3 assets"]}
          confirmLabel="Delete Template"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
